import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import useEditableQuestion from '@/data/hook/question/useEditableQuestion';
import Activity from '@/models/Activity';
import alert from '@/utils/shared/alert';
import { Question } from '@/models/Question';
import deleteQuestion from '@/utils/questions/deleteQuestion';
import { getQuestionErrorMessage } from '@/utils/questions/getQuestionErrorMessage';
type UseQuestionsProps = {
  activity: Activity;
  onDeleted: (editableActivity: Activity, target: Question) => void;
};

const useQuestions = ({ activity, onDeleted }: UseQuestionsProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab',
  });
  const { resolveEditable } = useEditableQuestion(activity);

  const questions = activity.questions ?? [];

  type DeleteArgs = {
    identifier: string;
    shouldShowAlert?: boolean;
  };

  const deleteMutation = useMutation({
    mutationFn: async ({ identifier }: DeleteArgs) => {
      const question = activity.questions?.find(
        q => q.identifier === identifier,
      );
      if (!question) throw new Error('Question not found');
      const { editableActivity, target } = await resolveEditable(question);
      await deleteQuestion({
        activityId: editableActivity.id,
        id: target.id,
        type: target.type,
      });
      return { editableActivity, target };
    },
    onSuccess: ({ editableActivity, target }, { shouldShowAlert }) => {
      onDeleted(editableActivity, target);
      if (shouldShowAlert) alert.success(t('deleteQuestionSuccess'));
    },
    onError: err => alert.error(getQuestionErrorMessage(err)),
  });

  return {
    questions,
    deleteQuestion: deleteMutation.mutate,
    deleteQuestionAsync: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
};

export default useQuestions;
